// src/pages/Pricing.js
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  SimpleGrid,
  Text,
  Heading,
  Button,
  Stack,
  Badge,
  useColorModeValue,
  Spinner,
  useToast,
  useBreakpointValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  HStack,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { loadStripe } from "@stripe/stripe-js";
import { useSwipeable } from "react-swipeable";
import { Capacitor } from '@capacitor/core';
import apiClient from "../api/apiClient";
import { useAuth } from "../contexts/AuthContext";
import useGooglePlay from "../hooks/useGooglePlay";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function Pricing() {
  const { user, fetchUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0); // swipe index
  const cancelRef = useRef();
  const toast = useToast();

  const [error, setError] = useState(null);
  const { subscribe, isInitialized } = useGooglePlay();

  const isMobile = useBreakpointValue({ base: true, md: false });

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColorDefault = useColorModeValue("gray.200", "gray.600");

  const planColors = {
    free: { base: "orange.50", border: "orange.400", button: "orange", badge: "orange", glow: "rgba(237,137,54,0.6)" },
    starter: { base: "blue.50", border: "blue.400", button: "blue", badge: "blue", glow: "rgba(66,153,225,0.6)" },
    pro: { base: "purple.50", border: "purple.500", button: "purple", badge: "purple", glow: "rgba(128,90,213,0.6)" },
  };

  const planOrder = ["free", "starter", "pro"];

  // ✅ Always declare hooks before any early return
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setCurrentIndex((prev) => Math.min(prev + 1, plans.length - 1)),
    onSwipedRight: () => setCurrentIndex((prev) => Math.max(prev - 1, 0)),
    trackMouse: true,
  });

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await apiClient.get("/plans");
        setPlans(res.data.plans || []);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
        setError("Failed to load plans. Please check your internet connection.");
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  if (!user || loadingPlans)
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" />
      </Box>
    );

  if (error) {
    return (
      <Box textAlign="center" py={20} px={5}>
        <Text color="red.500" mb={4}>{error}</Text>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }

  if (plans.length === 0) {
    return (
      <Box textAlign="center" py={20} px={5}>
        <Heading size="md">No plans available at the moment.</Heading>
        <Text mt={2} color="gray.500">Please try again later.</Text>
      </Box>
    );
  }

  const currentPlanId = user?.subscription?.plan || "free";
  const isCanceled = user?.subscription?.cancelAtPeriodEnd;
  const currentPeriodEnd = user?.subscription?.currentPeriodEnd
    ? new Date(user.subscription.currentPeriodEnd)
    : null;

  const canCancel =
    currentPlanId !== "free" &&
    !isCanceled &&
    currentPeriodEnd &&
    currentPeriodEnd > new Date();

  const isFree = currentPlanId === "free";

  const getPulse = (glow) => keyframes`
    0% { box-shadow: 0 0 10px 2px ${glow}; }
    50% { box-shadow: 0 0 20px 6px ${glow}; }
    100% { box-shadow: 0 0 10px 2px ${glow}; }
  `;

  const handleCheckout = async (planId) => {
    if (!user?._id) return toast({ title: "Login required", status: "warning" });

    // Native (Google Play)
    if (Capacitor.isNativePlatform()) {
      const productId = planId === 'starter' ? 'starter_monthly' : 'pro_monthly';
      
      if (!isInitialized) {
        toast({
          title: "Billing system initializing",
          description: "Please wait a moment and try again",
          status: "warning",
          duration: 3000,
        });
        return;
      }

      if (subscribe) {
        console.log(`🛒 Starting purchase for ${planId}`);
        subscribe(productId);
      } else {
        toast({ 
          title: "Purchase not available", 
          description: "Billing plugin is not ready",
          status: "error",
          duration: 4000,
        });
      }
      return;
    }

    // Web (Stripe)
    setProcessing(true);
    try {
      await stripePromise;
      const { data } = await apiClient.post("/payments/checkout", {
        plan: planId,
        userId: user._id,
      });
      if (data.url) window.location.href = data.url;
      else toast({ title: "Checkout failed", status: "error" });
    } catch (err) {
      console.error(err);
      toast({ title: "Checkout failed", status: "error" });
    } finally {
      setProcessing(false);
    }
  };

  const openConfirm = () => setIsOpen(true);
  const closeConfirm = () => setIsOpen(false);

  const handleCancelPlan = async () => {
    if (!user?._id) return;
    setProcessing(true);
    try {
      const { data } = await apiClient.post("/subscriptions/cancel");
      await fetchUser();
      toast({
        title: "Subscription canceled",
        description: data.message,
        status: "info",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed to cancel plan.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setProcessing(false);
      closeConfirm();
    }
  };

  return (
    <Box py={10} px={5} maxW="7xl" mx="auto">
      <Heading textAlign="center" mb={10}>
        Choose Your Plan
      </Heading>

      {isMobile ? (
        <Box overflow="hidden" position="relative">
          {/* Swipeable cards */}
          <Box
            display="flex"
            transition="transform 0.3s ease"
            transform={`translateX(-${currentIndex * 100}%)`}
            {...swipeHandlers}
          >
            {plans.map((plan) => {
              const colors = planColors[plan.id] || planColors.free;
              const planIndex = planOrder.indexOf(plan.id);
              const currentPlanIndex = planOrder.indexOf(currentPlanId);
              const isCurrent = plan.id === currentPlanId;
              const upgradeable = planIndex > currentPlanIndex;

              return (
                <Box key={plan.id} flex="0 0 100%" px={2} boxSizing="border-box">
                  <Box
                    p={6}
                    rounded="2xl"
                    shadow={isCurrent ? "xl" : "md"}
                    bg={isCurrent ? colors.base : cardBg}
                    borderWidth={isCurrent ? "2px" : "1px"}
                    borderColor={isCurrent ? colors.border : borderColorDefault}
                    position="relative"
                    transition="all 0.3s ease"
                    _hover={{ transform: "scale(1.02)", shadow: isCurrent ? "2xl" : "lg" }}
                    animation={isCurrent ? `${getPulse(colors.glow)} 2s infinite` : "none"}
                  >
                    {isCurrent && (
                      <Badge
                        colorScheme={colors.badge}
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        position="absolute"
                        top={3}
                        right={3}
                      >
                        CURRENT PLAN
                      </Badge>
                    )}

                    <Stack spacing={4} align="center">
                      <Heading size="lg" mt={isCurrent ? 4 : 0}>
                        {plan.name}
                      </Heading>

                      {isCurrent && currentPeriodEnd && !isFree && (
                        <Text fontSize="sm" color="gray.500">
                          {isCanceled
                            ? `Expires on ${currentPeriodEnd.toLocaleDateString()}`
                            : `Renew on ${currentPeriodEnd.toLocaleDateString()}`}
                        </Text>
                      )}

                      <Text fontSize="2xl" fontWeight="bold">
                        {plan.priceLabel || `$${plan.price}/month`}
                      </Text>

                      <Text textAlign="center">{plan.description || ""}</Text>

                      <Stack spacing={2} align="flex-start" mt={3}>
                        {plan.features.map((f, i) => (
                          <Text key={i}>• {f}</Text>
                        ))}
                      </Stack>

                      {isCurrent ? (
                        canCancel && (
                          <Button
                            w="full"
                            colorScheme="red"
                            onClick={openConfirm}
                            isLoading={processing}
                            mt={4}
                          >
                            Cancel
                          </Button>
                        )
                      ) : upgradeable ? (
                        <Button
                          w="full"
                          colorScheme={colors.button}
                          onClick={() => handleCheckout(plan.id)}
                          isLoading={processing}
                          isDisabled={Capacitor.isNativePlatform() && !isInitialized}
                          title={Capacitor.isNativePlatform() && !isInitialized ? 'Initializing billing...' : ''}
                          mt={4}
                        >
                          {Capacitor.isNativePlatform() && !isInitialized ? 'Initializing...' : 'Upgrade'}
                        </Button>
                      ) : (
                        <Button w="full" colorScheme="gray" isDisabled mt={4}>
                          Locked
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Dots indicator */}
          <HStack justify="center" spacing={2} mt={4}>
            {plans.map((_, idx) => (
              <Box
                key={idx}
                w={idx === currentIndex ? 3 : 2}
                h={idx === currentIndex ? 3 : 2}
                borderRadius="full"
                bg={idx === currentIndex ? "blue.500" : "gray.300"}
              />
            ))}
          </HStack>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {plans.map((plan) => {
            const colors = planColors[plan.id] || planColors.free;
            const planIndex = planOrder.indexOf(plan.id);
            const currentPlanIndex = planOrder.indexOf(currentPlanId);
            const isCurrent = plan.id === currentPlanId;
            const upgradeable = planIndex > currentPlanIndex;

            return (
              <Box
                key={plan.id}
                p={8}
                rounded="2xl"
                shadow={isCurrent ? "xl" : "md"}
                bg={isCurrent ? colors.base : cardBg}
                borderWidth={isCurrent ? "2px" : "1px"}
                borderColor={isCurrent ? colors.border : borderColorDefault}
                position="relative"
                transition="all 0.3s ease"
                _hover={{ transform: "scale(1.05)", shadow: isCurrent ? "2xl" : "lg" }}
                animation={isCurrent ? `${getPulse(colors.glow)} 2s infinite` : "none"}
              >
                {isCurrent && (
                  <Badge
                    colorScheme={colors.badge}
                    fontSize="xs"
                    px={3}
                    py={1}
                    borderRadius="full"
                    position="absolute"
                    top={3}
                    right={3}
                  >
                    CURRENT PLAN
                  </Badge>
                )}

                <Stack spacing={4} align="center">
                  <Heading size="lg" mt={isCurrent ? 4 : 0}>
                    {plan.name}
                  </Heading>

                  {isCurrent && currentPeriodEnd && !isFree && (
                    <Text fontSize="sm" color="gray.500">
                      {isCanceled
                        ? `Expires on ${currentPeriodEnd.toLocaleDateString()}`
                        : `Renew on ${currentPeriodEnd.toLocaleDateString()}`}
                    </Text>
                  )}

                  <Text fontSize="2xl" fontWeight="bold">
                    {plan.priceLabel || `$${plan.price}/month`}
                  </Text>

                  <Text textAlign="center">{plan.description || ""}</Text>

                  <Stack spacing={2} align="flex-start" mt={3}>
                    {plan.features.map((f, i) => (
                      <Text key={i}>• {f}</Text>
                    ))}
                  </Stack>

                  {isCurrent ? (
                    canCancel && (
                      <Button
                        w="full"
                        colorScheme="red"
                        onClick={openConfirm}
                        isLoading={processing}
                        mt={4}
                      >
                        Cancel
                      </Button>
                    )
                  ) : upgradeable ? (
                    <Button
                      w="full"
                      colorScheme={colors.button}
                      onClick={() => handleCheckout(plan.id)}
                      isLoading={processing}
                      isDisabled={Capacitor.isNativePlatform() && !isInitialized}
                      title={Capacitor.isNativePlatform() && !isInitialized ? 'Initializing billing...' : ''}
                      mt={4}
                    >
                      {Capacitor.isNativePlatform() && !isInitialized ? 'Initializing...' : 'Upgrade'}
                    </Button>
                  ) : (
                    <Button w="full" colorScheme="gray" isDisabled mt={4}>
                      Locked
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeConfirm}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cancel Subscription
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to cancel your subscription? You will retain
              access until the end of your current billing period.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeConfirm}>
                No, keep it
              </Button>
              <Button
                colorScheme="red"
                onClick={handleCancelPlan}
                ml={3}
                isLoading={processing}
              >
                Yes, cancel
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
